import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { Tooltip } from "components";

type PracticeTableTooltipProps = {
  title: React.ReactNode;
  children: (args: { ref: React.RefCallback<HTMLElement> }) => React.ReactNode;
  alwaysShow?: boolean;
};

// shared by historical-practice-data-table-view/MissingDataTable.tsx and all-fields-data-table-view/AllFieldsDataTable.tsx
export const PracticeTableTooltip = ({ title, children, alwaysShow = false }: PracticeTableTooltipProps) => {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    if (alwaysShow || !targetEl) return;

    const next = targetEl.scrollWidth > targetEl.clientWidth || targetEl.scrollHeight > targetEl.clientHeight;

    setIsOverflowing((prev) => (prev === next ? prev : next));
  }, [alwaysShow, targetEl]);

  useLayoutEffect(() => {
    if (alwaysShow) return;
    measure();
  }, [alwaysShow, measure, title]);

  useEffect(() => {
    if (alwaysShow || !targetEl) return;

    // prefer ResizeObserver for accurate overflow detection
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => measure());
      ro.observe(targetEl);
      return () => ro.disconnect();
    }

    // fallback: window resize
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [alwaysShow, targetEl, measure]);

  return (
    <Tooltip
      title={alwaysShow || isOverflowing ? title : undefined}
      arrow={false}
      classNames={{ root: "historical-practice-data-table-view-tooltip" }}
      className="historical-practice-data-table-view-tooltip-span"
    >
      {children({ ref: setTargetEl })}
    </Tooltip>
  );
};
